import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../Store'
import { IBpAlertProps } from '../../../shared/components/cards/alert/BpAlert'
import { ReactNode } from 'react'
import { Location } from 'react-router-dom'
import { IOption } from '../../../shared/components/forms/search-input/BpSearchInput'
import {
  DEFAULT_MENU_LAYOUT,
  getStoredSidebarExpanded,
  TMenuLayout,
} from '../../../shared/consts/MenuLayout.const'
import { login } from './AuthSlice'

export type TSizeModal = 'md' | 'lg' | 'xl' | 'full'

type AppState = {
  alerts: IBpAlertProps[]
  canCloseDropdown: boolean
  modals: {
    id?: string
    size?: TSizeModal
    width?: number
    scroll?: boolean
    withBackButton?: boolean
    children: ReactNode
    onClose?: () => void
  }[]
  location: {
    current?: Location
    previous?: Location
  }
  demoProject?: IOption
  showGuide?: Date
  menuLayout: TMenuLayout
  sidebarExpanded: boolean
}

const appSlice = createSlice({
  name: 'app',
  initialState: {
    alerts: [],
    canCloseDropdown: true,
    modals: [],
    location: {},
    menuLayout: DEFAULT_MENU_LAYOUT,
    sidebarExpanded: getStoredSidebarExpanded(),
  } as AppState,
  reducers: {
    showAlert: (state: AppState, { payload }: PayloadAction<IBpAlertProps>) => {
      if (state.alerts.find((alert) => alert.message == payload.message)) return
      state.alerts?.push(payload)
    },
    hideAlert: (state: AppState, { payload }: PayloadAction<string>) => {
      state.alerts = []
    },
    setCanCloseDropdown: (state: AppState, action: PayloadAction<boolean>) => {
      state.canCloseDropdown = action.payload
    },
    setModal: (
      state: AppState,
      action: PayloadAction<{
        id?: string
        size?: TSizeModal
        width?: number
        scroll?: boolean
        withBackButton?: boolean
        children: ReactNode
        onClose?: () => void
      }>,
    ) => {
      state.modals?.push(action.payload)
    },
    hideModal: (state: AppState) => {
      state.modals = state.modals.slice(0, -1)
    },
    hideAllModal: (state: AppState) => {
      state.modals = []
    },
    setLocation: (state: AppState, action: PayloadAction<Location>) => {
      if (state.location.current?.pathname !== action.payload.pathname) {
        state.location.previous = state.location.current
      }

      state.location.current = action.payload
    },
    setDemoProject: (state: AppState, action: PayloadAction<IOption | undefined>) => {
      state.demoProject = action.payload
    },
    setShowGuide: (state: AppState) => {
      state.showGuide = new Date()
    },
    setMenuLayout: (state: AppState, action: PayloadAction<TMenuLayout>) => {
      state.menuLayout = action.payload
    },
    setSidebarExpanded: (state: AppState, action: PayloadAction<boolean>) => {
      state.sidebarExpanded = action.payload
    },
  },
  extraReducers: (builder) => {
    // Расположение меню хранится на бэке и приезжает с currentuser — он и источник правды
    builder.addCase(login, (state: AppState, { payload }) => {
      state.menuLayout = payload.front_menu_position ?? DEFAULT_MENU_LAYOUT
    })
  },
})

export const {
  showAlert,
  hideAlert,
  setCanCloseDropdown,
  setModal,
  hideModal,
  hideAllModal,
  setLocation,
  setDemoProject,
  setShowGuide,
  setMenuLayout,
  setSidebarExpanded,
} = appSlice.actions

export const selectAlerts = (state: RootState) => state.root.app.alerts
export const selectModals = (state: RootState) => state.root.app.modals
export const selectCanCloseDropdown = (state: RootState) => state.root.app.canCloseDropdown
export const selectPreviousLocation = (state: RootState) => state.root.app.location.previous
export const selectDemoProject = (state: RootState) => state.root.app.demoProject
export const selectShowGuide = (state: RootState) => state.root.app.showGuide
export const selectMenuLayout = (state: RootState) => state.root.app.menuLayout
export const selectSidebarExpanded = (state: RootState) => state.root.app.sidebarExpanded

export default appSlice.reducer
